import { formatShortDateWithWeekday, formatTime } from "@clipboard-health/date-time";
import { Pill } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { type Theme } from "@mui/material";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";

interface InterviewStatusPillProps {
  status: InterviewStatus;
  documentDeadline?: Date;
  timezone?: string;
}

const statusConfig = {
  [InterviewStatus.PENDING]: {
    label: "Pending",
    backgroundColor: (theme: Theme) => theme.palette.intent?.warning.background,
    color: (theme: Theme) => theme.palette.intent?.warning.text,
  },
  [InterviewStatus.BOOKED]: {
    label: "Booked",
    backgroundColor: (theme: Theme) => theme.palette.intent?.success.background,
    color: (theme: Theme) => theme.palette.intent?.success.text,
  },
  [InterviewStatus.CANCELLED]: {
    label: "Cancelled",
    backgroundColor: (theme: Theme) => theme.palette.intent?.warning.background,
    color: (theme: Theme) => theme.palette.intent?.warning.text,
  },
  [InterviewStatus.RESCHEDULED]: {
    label: "Rescheduled",
    backgroundColor: (theme: Theme) => theme.palette.intent?.warning.background,
    color: (theme: Theme) => theme.palette.intent?.warning.text,
  },
  [InterviewStatus.ATTENDANCE_CONFIRMED]: {
    label: "Attended",
    backgroundColor: (theme: Theme) => theme.palette.intent?.neutral.background,
    color: (theme: Theme) => theme.palette.intent?.neutral.text,
  },
  [InterviewStatus.NO_CALL_NO_SHOW]: {
    label: "No Show",
    backgroundColor: (theme: Theme) => theme.palette.intent?.error.background,
    color: (theme: Theme) => theme.palette.intent?.error.text,
  },
};

/**
 * Formats time in a compact format (e.g., "4pm" instead of "4:00 PM")
 * Removes minutes when they are :00 and converts to lowercase
 */
function formatCompactTime(date: Date, timezone: string): string {
  const time = formatTime(date, { timeZone: timezone });
  return time.replace(":00", "").replace(" ", "").toLowerCase();
}

function getPendingLabel(documentDeadline?: Date, timezone?: string): string {
  if (!isDefined(documentDeadline) || !isDefined(timezone)) {
    return "Pending";
  }

  const deadlineDate = formatShortDateWithWeekday(documentDeadline, { timeZone: timezone });
  const deadlineTime = formatCompactTime(documentDeadline, timezone);

  return `Upload Documents by ${deadlineDate} ${deadlineTime}`;
}

export function InterviewStatusPill(props: InterviewStatusPillProps) {
  const { status, documentDeadline, timezone } = props;
  const config = statusConfig[status];

  const label =
    status === InterviewStatus.PENDING ? getPendingLabel(documentDeadline, timezone) : config.label;

  return (
    <Pill
      label={label}
      variant="filled"
      size="small"
      backgroundColor={config.backgroundColor}
      color={config.color}
    />
  );
}
